/**
* Copyright by: 

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { Injectable } from '@angular/core';
import { AppConfigService, Logger, LogService } from '@epg/angular-base-lib';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { SYSTEM_DISPLAY_NAME } from '../app/shared/constants';
import { sortArrayBy } from '../convenient-functions/array-sort';
import { EmployeeGroupDto } from '../models/dtos/employee-group.dto';
import { CreateEmployeeDto, EmployeeDto } from '../models/dtos/employee.dto';
import { EmployeeGroupModel } from '../models/employee-group.model';
import { EmployeeModel } from '../models/employee.model';
import { SearchModel } from '../models/search.model';
import { ApiService } from './api.service';
import { DialogService } from './dialog.service';
import { SnackbarService } from './snackbar.service';
import { HttpResponse } from '@angular/common/http';
import { TenantManagerClientService } from './tenant-manager-client.service';
import { TenantMappingService } from './tenant-mapping.service';
import { LicenseDto } from '@epg/tenant-manager-client';

type AccountServiceInfoResponse = {
  is_service_account?: boolean;
};

@Injectable({
  providedIn: 'root'
})
export class EmployeeService {
  employeesChanged = new Subject<EmployeeModel[]>();
  employeesSearchChanged = new Subject<EmployeeModel[]>();
  employeeGroupsChanged = new Subject<EmployeeGroupModel[]>();
  employeeRouteChange = new Subject<string>();
  employeeGroupRouteChange = new Subject<string>();

  employees: EmployeeModel[] = [];
  employeeGroups: EmployeeGroupModel[] = [];
  employeeSearchList: EmployeeModel[] = [];
  private accountDisplayNameCache = new Map<string, string>();

  private log: Logger;
  constructor(
    private snackbarService: SnackbarService,
    private apiService: ApiService,
    private dialogService: DialogService,
    private logService: LogService,
    private tenantManagerClientService: TenantManagerClientService,
    private tenantMappingService: TenantMappingService
  ) {
    this.log = logService.getLogger(this);
    this.getEmployees();
  }

  //#region Employee

  async getEmployees(): Promise<EmployeeModel[]> {
    return await new Promise(async (resolve, reject) => {
      const searchBody = {
        search_criteria: {},
        sort: 'user_name',
        select: '_id full_name email user_name employee_group',
        skip: 0,
        limit: 0
      };
      await this._searchEmployees(searchBody)
        .then((employees: EmployeeModel[]) => {
          this.employees = employees;
          this.employeesChanged.next(this.employees.slice());
          resolve(this.employees.slice());
        })
        .catch((error) => {
          this.log.warning(error);
          resolve(error);
        });
    });
  }

  // returns the employeelist from the current search query
  async searchEmployees(searchBody: SearchModel) {
    this.employeeSearchList = await this._searchEmployees(searchBody);
    this.employeesSearchChanged.next(this.employeeSearchList);
  }

  async _searchEmployees(searchBody: SearchModel): Promise<EmployeeModel[]> {
    return await new Promise((resolve, reject) => {
      this.apiService
        .search('search/employee', searchBody)
        .pipe(
          map((response: HttpResponse<EmployeeDto[]>) => {
            let employeeDtos: EmployeeDto[] = response.body ?? [];
            let employees: EmployeeModel[] = [];
            for (var employeeDto of employeeDtos) {
              employees.push(this.employeeDtoToModel(employeeDto));
            }
            return employees;
          })
        )
        .subscribe(
          (employees) => {
            resolve(employees);
          },
          (error) => {
            this.log.error(error);
            reject(error);
          }
        );
    });
  }

  // return one employee
  getEmployee(id: string): Promise<EmployeeModel> {
    return new Promise((resolve, reject) => {
      this.apiService
        .get(`employee/${id}`)
        .pipe(
          map(async (employeeDto: EmployeeDto) => {
            return await this.employeeDtoToModel(employeeDto);
          })
        )
        .subscribe(
          (employee) => {
            resolve(employee);
          },
          (error) => {
            this.log.error(error);
            reject(error);
          }
        );
    });
  }

  // add Employee
  async addEmployee(employee: EmployeeModel): Promise<string> {
    return await new Promise((resolve, reject) => {
      this.apiService
        .post('employee', this.employeeModelToCreateEmployeeDto(employee))
        .pipe(map((employeeDto: EmployeeDto) => this.employeeDtoToModel(employeeDto)))
        .subscribe(
          (newEmployee: EmployeeModel) => {
            if (
              this.employees.findIndex((employee) => employee.userName == newEmployee.userName) ==
              -1
            ) {
              this.employees.push(newEmployee);
              sortArrayBy(this.employees, 'email');
              this.employeesChanged.next(this.employees.slice());
            }
            this.employeeRouteChange.next(newEmployee.employeeId);
            resolve(newEmployee.employeeId);
          },
          (error) => {
            this.log.error(error);
            reject(error);
          }
        );
    });
  }

  // update Employee
  updateEmployee(employee: EmployeeModel) {
    // TODO: right now put request to backend doesnt accept _id, so we have to manipulate the dto once again.
    // this backend behaviour could be changed in the future, then we no longer need this manipulation of Dto object.
    const employeeDto = this.employeeModelToDto(employee);
    const body = {
      name: employeeDto.full_name,
      email: employeeDto.email,
      employee_group: employeeDto.employee_group,
      start_screen_configuration: employeeDto.start_screen_configuration
    };
    this.apiService
      .put('employee/' + employee.employeeId, this.employeeModelToCreateEmployeeDto(employee))
      .pipe(map((employeeDto: EmployeeDto) => this.employeeDtoToModel(employeeDto)))
      .subscribe(
        (newEmployee: EmployeeModel) => {
          const index = this.employees.findIndex((x) => x.employeeId === newEmployee.employeeId);
          // delete
          this.employees.splice(index, 1);
          // add to the same index
          this.employees.splice(index, 0, newEmployee);
          sortArrayBy(this.employees, 'email');
          this.employeesChanged.next(this.employees.slice());
          // this.setCurrentEmployee(newEmployee);
        },
        (error) => {
          this.log.error(error);
        }
      );
  }

  async changeDefaultTenant(username: string, tenant_id: string): Promise<void> {
    return await new Promise(async (resolve, reject) => {
      if (this.employees.length === 0) {
        await this.getEmployees();
      }
      let employee = this.employees.find((emp) => emp.userName === username);
      if (!employee) {
        const employeeListEmptyMessage = $localize`:employee list@@changeDefaultTenant:Could not switch to the company ${tenant_id}, employee list is empty`;
        reject(employeeListEmptyMessage);
      }
      if (employee) {
        this.apiService
          .put('employee/' + employee.employeeId, {
            default_tenant: tenant_id,
            user_name: employee.userName
          })
          .pipe(map((employeeDto: EmployeeDto) => this.employeeDtoToModel(employeeDto)))
          .subscribe(
            (newEmployee: EmployeeModel) => {
              resolve();
            },
            (error) => {
              const updateFaileMessage = $localize`:default tenant update failed@@changeDefaultTenantUpdate:Could not switch to the company ${tenant_id}, default_tenant updation failed.`;
              reject(updateFaileMessage);
              this.log.error(error);
            }
          );
      }
    });
  }

  // delete Employee
  deleteEmployee(employee: EmployeeModel) {
    this.apiService.delete('employee/' + employee.employeeId).subscribe(
      (employeeDto: EmployeeDto) => {
        let index = this.employees.findIndex((x) => x.employeeId === employee.employeeId);
        this.employees.splice(index, 1);
        if (index !== 0) {
          index -= 1;
        }
        if (this.employees.length >= index && this.employees.length > 0) {
          this.employeeRouteChange.next(this.employees[index].employeeId);
        } else {
          this.employeeRouteChange.next('new');
        }
        this.employeesChanged.next(this.employees.slice());
        // this.snackbarService.showSnackbar('Die Mitarbeiterdaten wurden gelöscht');
      },
      (error) => {
        this.log.error(error);
        this.dialogService.showErrorDialog(error.error.message);
      }
    );
  }

  employeeDtoToModel(employeeDto: EmployeeDto): EmployeeModel {
    return new EmployeeModel(
      employeeDto._id,
      employeeDto.full_name,
      employeeDto.email,
      employeeDto.user_name,
      employeeDto.employee_group,
      employeeDto.start_screen_configuration
    );
  }

  employeeModelToDto(employeeModel: EmployeeModel): EmployeeDto {
    return new EmployeeDto(
      employeeModel.employeeId,
      employeeModel.name,
      employeeModel.email,
      employeeModel.userName,
      employeeModel.employeeGroup,
      employeeModel.startScreenConfiguration
    );
  }

  employeeModelToCreateEmployeeDto(employeeModel: EmployeeModel): CreateEmployeeDto {
    return new CreateEmployeeDto(
      employeeModel.name,
      employeeModel.email,
      employeeModel.userName,
      employeeModel.employeeGroup,
      employeeModel.startScreenConfiguration
    );
  }

  //#endregion

  //#region EmployeeGroup

  async getEmployeeGroups(searchBody?: SearchModel): Promise<EmployeeGroupModel[]> {
    // get list of employeegroups
    return await new Promise((resolve, reject) => {
      if (!searchBody) {
        searchBody = {
          search_criteria: {},
          sort: 'name',
          select: '',
          skip: 0,
          limit: 0
        };
      }
      this.apiService
        .search('search/employeegroup', searchBody)
        .pipe(
          map((response: HttpResponse<EmployeeGroupDto[]>) => {
            let employeeGroupDtos: EmployeeGroupDto[] = response.body ?? [];
            let employeeGroups: EmployeeGroupModel[] = [];
            for (var employeeGroupDto of employeeGroupDtos) {
              employeeGroups.push(this.employeeGroupDtoToModel(employeeGroupDto));
            }
            return employeeGroups;
          })
        )
        .subscribe(
          (empGroups) => {
            this.employeeGroups = empGroups;
            this.employeeGroupsChanged.next(this.employeeGroups.slice());
            resolve(this.employeeGroups);
          },
          (error) => {
            this.log.error(error);
            reject(error);
          }
        );
    });
  }

  async getEmployeesInEmployeeGroup(employeeGroupId: string): Promise<string[]> {
    const searchCriteria: SearchModel = {
      search_criteria: { employee_group: employeeGroupId },
      skip: 0,
      limit: 0,
      sort: '',
      select: '_id'
    };
    return await new Promise<string[]>((resolve, reject) => {
      this.apiService
        .post('search/employee', searchCriteria)
        .pipe(
          map((employees) => {
            let empIds: string[] = [];
            for (let index = 0; index < employees.length; index++) {
              const element = employees[index];
              empIds.push(element._id);
            }

            return empIds;
          })
        )
        .subscribe(
          (employees) => {
            resolve(employees);
          },
          (error) => {
            this.log.error(error);
            reject(error);
          }
        );
    });
  }

  // return one employee group / undefined
  getEmployeeGroup(id: string): EmployeeGroupModel | undefined {
    return this.employeeGroups.find((x) => x.employeeGroupId == id);
  }

  // add employee group
  async addEmployeeGroup(empGroup: EmployeeGroupModel) {
    const existingGroup = this.employeeGroups.find(
      (g) => g.name.toLowerCase() === empGroup.name.toLowerCase()
    );
    if (existingGroup) {
      this.snackbarService.showSnackBar(
        $localize`:@@groupNameDuplicate:A group with this name already exists`
      );
      this.employeeGroupRouteChange.next(existingGroup.employeeGroupId);
      return;
    }

    const body = { name: empGroup.name, supervisors: empGroup.supervisors };

    const newEmployeeGroup = await this._addEmployeeGroup(body);

    await this.updateEmployeesToEmployeeGroup(newEmployeeGroup.employeeGroupId, empGroup.employees);
    sortArrayBy(this.employeeGroups, 'name');
    await this._addEmployeesToEmployeeGroup(newEmployeeGroup.employeeGroupId);
    this.employeeGroupsChanged.next(this.employeeGroups.slice());

    // route to new url
    this.employeeGroupRouteChange.next(newEmployeeGroup.employeeGroupId);
  }

  private async _addEmployeeGroup(body: any): Promise<EmployeeGroupModel> {
    return await new Promise<EmployeeGroupModel>((resolve, reject) => {
      this.apiService
        .post('employeegroup', body)
        .pipe(
          map((employeeGroupDto: EmployeeGroupDto) =>
            this.employeeGroupDtoToModel(employeeGroupDto)
          )
        )
        .subscribe(
          (newEmployeeGroup: EmployeeGroupModel) => {
            this.employeeGroups.push(newEmployeeGroup);
            resolve(newEmployeeGroup);
          },
          (error) => {
            this.log.error(error);
            reject(error);
          }
        );
    });
  }

  // update employees in a employee group
  async updateEmployeesToEmployeeGroup(employeeGroupId: string, employeeIds: string[]) {
    await new Promise((resolve, reject) => {
      this.apiService
        .post(`employeegroup/${employeeGroupId}/setemployees`, { employees: employeeIds })
        .subscribe(
          async (msg) => {
            this.log.debug(msg);

            resolve(true);
          },
          (error) => {
            this.log.error(error);
            reject(error);
          }
        );
    });
  }

  _addEmployeesToEmployeeGroup(employeeGroupId: string) {
    return new Promise<void>(async (resolve, reject) => {
      const index = this.employeeGroups.findIndex((x) => x.employeeGroupId === employeeGroupId);
      this.employeeGroups[index].employees = await this.getEmployeesInEmployeeGroup(
        employeeGroupId
      ).catch((error) => {
        this.log.error(error);
        reject(error);
        return [];
      });
      resolve();
    });
  }

  // update Employee Group
  async updateEmployeeGroup(employeeGroup: EmployeeGroupModel) {
    // TODO: right now put request to backend doesnt accept _id, so we have to manipulate the dto once again.
    // this backend behaviour could be changed in the future, then we no longer need this manipulation of Dto object.
    const body = {
      name: employeeGroup.name,
      default: employeeGroup.defaultGroup,
      start_screen_configuration: employeeGroup.startScreenConfiguration,
      supervisors: employeeGroup.supervisors
    };

    await this._updateEmployeeGroup(employeeGroup.employeeGroupId, body);

    sortArrayBy(this.employeeGroups, 'name');

    await this.updateEmployeesToEmployeeGroup(
      employeeGroup.employeeGroupId,
      employeeGroup.employees
    );
    await this._addEmployeesToEmployeeGroup(employeeGroup.employeeGroupId);
    this.employeeGroupsChanged.next(this.employeeGroups.slice());
  }

  private async _updateEmployeeGroup(employeeGroupId: string, body: any) {
    await new Promise((resolve, reject) => {
      this.apiService
        .put('employeegroup' + '/' + employeeGroupId, body)
        .pipe(
          map((employeeGroupDto: EmployeeGroupDto) =>
            this.employeeGroupDtoToModel(employeeGroupDto)
          )
        )
        .subscribe(
          (newEmployeeGroup: EmployeeGroupModel) => {
            const index = this.employeeGroups.findIndex(
              (x) => x.employeeGroupId === newEmployeeGroup.employeeGroupId
            );
            // delete
            this.employeeGroups.splice(index, 1);
            // add to the same index
            this.employeeGroups.splice(index, 0, newEmployeeGroup);
            sortArrayBy(this.employeeGroups, 'name');
            resolve(true);
          },
          (error) => {
            this.log.error(error);
            reject(error);
          }
        );
    });
  }

  // delete Employee
  deleteEmployeeGroup(id: string) {
    this.apiService
      .delete(`employeegroup/${id}`)
      // .deleteEmployeeGroup(id)
      .pipe(
        map((employeeGroupDto: EmployeeGroupDto) => this.employeeGroupDtoToModel(employeeGroupDto))
      )
      .subscribe(
        (empGroup: EmployeeGroupModel) => {
          let index = this.employeeGroups.findIndex(
            (x) => x.employeeGroupId === empGroup.employeeGroupId
          );
          this.employeeGroups.splice(index, 1);

          this.employeeGroupsChanged.next(this.employeeGroups.slice());

          // route to new url
          if (index != 0) {
            index -= 1;
          }
          let url = 'new';
          if (this.employeeGroups.length != 0) {
            url = this.employeeGroups[index].employeeGroupId;
          }
          this.employeeGroupRouteChange.next(url);
        },
        (error) => {
          this.log.error(error);
          this.dialogService.showErrorDialog(error.error.message);
        }
      );
  }

  async getTotalLicenses(): Promise<number> {
    try {
      const tenantName = this.tenantMappingService.activeTenant?.name;
      if (!tenantName) {
        this.log.warning('No active tenant found for license retrieval');
        return 0;
      }
      const licenses: LicenseDto[] = await this.tenantManagerClientService.client
        .tenants(tenantName)
        .applications('oneapp')
        .licenses()
        .get();
      const activeLicenses = licenses.filter(
        // `created` is no longer part of LicenseState, but can still be returned by older tenant managers
        (license) => license.state === 'verified' || (license.state as string) === 'created'
      );
      let totalUnits = 0;
      for (const license of activeLicenses) {
        if (license.modules) {
          for (const module of license.modules) {
            totalUnits += module.units;
          }
        }
      }
      return totalUnits;
    } catch (error) {
      this.log.error(error);
      return 0;
    }
  }

  // convert employeeGroup DTO to Model
  employeeGroupDtoToModel(employeeGroupDto: EmployeeGroupDto): EmployeeGroupModel {
    return new EmployeeGroupModel(
      employeeGroupDto._id,
      employeeGroupDto.name,
      [],
      employeeGroupDto.default_group,
      employeeGroupDto.start_screen_configuration,
      false,
      employeeGroupDto.supervisors ?? []
    );
  }

  getEmployeeNameFromID(id: string): string | undefined {
    return this.employees.find((emp) => emp.employeeId == id)?.name;
  }

  getEmployeeUserNameFromID(id: string): string | undefined {
    return this.employees.find((emp) => emp.employeeId == id)?.userName;
  }

  async getDisplayNameForUserOrServiceAccount(id: string): Promise<string> {
    const employeeUserName = this.getEmployeeUserNameFromID(id);
    if (employeeUserName) {
      return employeeUserName;
    }

    const cachedValue = this.accountDisplayNameCache.get(id);
    if (cachedValue && cachedValue !== id) {
      return cachedValue;
    }

    const resolvedValue = await this.resolveAccountDisplayNameByBackend(id);
    if (resolvedValue !== id) {
      this.accountDisplayNameCache.set(id, resolvedValue);
    } else {
      this.accountDisplayNameCache.delete(id);
    }
    return resolvedValue;
  }

  private async resolveAccountDisplayNameByBackend(id: string): Promise<string> {
    return await new Promise((resolve) => {
      this.apiService.get(`employee/account-service-info/${id}`).subscribe(
        (result: AccountServiceInfoResponse) => {
          if (result?.is_service_account === true) {
            resolve(SYSTEM_DISPLAY_NAME);
            return;
          }

          resolve(id);
        },
        (error) => {
          this.log.warning(error);
          resolve(id);
        }
      );
    });
  }

  getEmployeeGroupNameFromID(id: string): string | undefined {
    return this.employeeGroups.find((empGrp) => empGrp.employeeGroupId == id)?.name;
  }

  //#endregion
}
